import { useState } from "react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import { DomainPill } from "@/presentation/components/roadmaps/domain-pill";
import type { Domain } from "@/presentation/data/domains";
import { VERDICT_COLOR, VERDICT_DESCRIPTION, VERDICT_LABEL } from "@/presentation/data/roadmaps";
import type { PendingDrop } from "@/presentation/hooks/use-branch-board-page";

interface DropDialogProps {
  pending: PendingDrop | null;
  onConfirm: (notes: string | null) => void;
  onCancel: () => void;
}

export function DropDialog({ pending, onConfirm, onCancel }: DropDialogProps) {
  return (
    <Dialog open={pending !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-md">
        {pending ? (
          <DropDialogBody
            key={`${pending.feature.id}-${pending.verdict}`}
            pending={pending}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DropDialogBody({
  pending,
  onConfirm,
  onCancel,
}: {
  pending: PendingDrop;
  onConfirm: (notes: string | null) => void;
  onCancel: () => void;
}) {
  const [notes, setNotes] = useState("");
  const accent = VERDICT_COLOR[pending.verdict];
  const domain = pending.feature.domain as Domain | null;
  return (
    <>
      <DialogHeader>
        <DialogTitle>Assign verdict</DialogTitle>
        <DialogDescription>{VERDICT_DESCRIPTION[pending.verdict]}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="bg-muted/40 rounded-md border px-3 py-2">
          <p className="text-foreground text-sm font-medium">{pending.feature.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {domain ? <DomainPill domain={domain} size="sm" /> : null}
            <span
              className="font-data inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold tracking-tight uppercase"
              style={{ background: `${accent}1a`, color: accent }}
            >
              {VERDICT_LABEL[pending.verdict]}
            </span>
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-foreground text-xs font-medium">Notes (optional)</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. extend WMS lot tracking, Salesforce candidate, …"
            rows={3}
            autoFocus
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onConfirm(notes)} style={{ background: accent }}>
          Place
        </Button>
      </DialogFooter>
    </>
  );
}
